import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Form, Button } from 'antd';
import SuppliersReportForm from '../SuppliersReportForm';
import { ModalProps } from '@/types/ModalProps';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import { useGenerateSupplierReport } from '@/core/domain/reports/people/Report.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportSuppliersFormValue } from '@/core/domain/reports/people/Report.types';
import { ReportCallback } from '@/hooks/useReportGenerate';

function SuppliersReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ReportSuppliersFormValue>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const mutation = useGenerateSupplierReport();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();
      const activeMap = {
        0: undefined,
        1: true,
        2: false,
      };

      const obj = {
        ...values,
        format: FILE_FORMAT[format],
        active: activeMap[values.active],
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      handleVisible();
      form.resetFields();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <Modal
      width={680}
      destroyOnClose
      visible={visible}
      onCancel={() => {
        handleVisible();
        form.resetFields();
      }}
      title={intl.formatMessage({ id: 'suppliers.modal.report.title' })}
      footer={[
        <Button
          type="primary"
          onClick={() =>
            handleGenerateReport('REPORT_VIEW', (result) =>
              report.openFile({
                result,
                fileName: 'fornecedores.pdf',
                fileType: 'application/pdf',
              }),
            )
          }
          key="viewReport"
          loading={fileLoading.REPORT_VIEW}
        >
          <FormattedMessage id="suppliers.modal.report.buttons.viewReport" />
        </Button>,
        <Button
          type="primary"
          onClick={() =>
            handleGenerateReport('PDF', (result) =>
              report.downloadFile({ result, fileName: 'fornecedores.pdf' }),
            )
          }
          key="PDF"
          loading={fileLoading.PDF}
        >
          <FormattedMessage id="suppliers.modal.report.buttons.pdf" />
        </Button>,
        <Button
          type="primary"
          onClick={() =>
            handleGenerateReport('CSV', (result) =>
              report.downloadFile({ result, fileName: 'fornecedores.csv' }),
            )
          }
          key="CSV"
          loading={fileLoading.CSV}
        >
          <FormattedMessage id="suppliers.modal.report.buttons.csv" />
        </Button>,
      ]}
    >
      <SuppliersReportForm form={form} />
    </Modal>
  );
}

export default SuppliersReportModal;
